import { useCallback, useEffect, useRef, useState } from "react";
import { EnumSingleSelectFieldV2Props } from "../../../../";
import {
  DropDownListModel,
  DropDownListComponent,
  DropDownListTypecast,
  FilteringEventArgs,
  SelectEventArgs,
} from "@syncfusion/ej2-react-dropdowns";
import { DefaultHtmlAttributes } from "@syncfusion/ej2-react-base";
import { DataManager, JsonAdaptor, Query } from "@syncfusion/ej2-data";
import { EnumSingleSelectFieldValue, Option } from "../../types/fieldTypes";
import { isEqual, omit } from "lodash";

type OptionValues = { [key: string]: Object }[];
export type EnumSingleSelectPropType = DropDownListModel &
  DefaultHtmlAttributes &
  DropDownListTypecast;

export const useEnumSingleSelectFieldV2 = (
  props: EnumSingleSelectFieldV2Props
) => {
  const dataSourceRef = useRef<
    { [key: string]: Object }[] | DataManager | string[] | number[] | boolean[]
  >(null);
  const [settings, setSettings] = useState<EnumSingleSelectPropType>();
  const [localValue, setLocalValue] = useState<EnumSingleSelectFieldValue>(
    props.value
  );
  const [ready, setReady] = useState<boolean>(false);
  const singleSelectRef = useRef<DropDownListComponent>(null);

  useEffect(() => {
    if (!ready) {
      return;
    }
    setLocalValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (!singleSelectRef.current) {
      return;
    }

    if (
      props.value !== localValue ||
      props.value !== singleSelectRef.current.value // Makes it work when using the props to set when a value was never set.
    ) {
      if (localValue === undefined) {
        singleSelectRef.current.clear();
      } else {
        singleSelectRef.current.value = localValue;
      }
      props.onSubmit?.(localValue);
    }
  }, [localValue]);

  useEffect(() => {
    setup();
  }, [props.disabled, props.optionValues, props.reorderOptions]);

  const filteringHandler = (e: FilteringEventArgs) => {
    let query: Query = new Query();
    // Frame the query based on search string with filter type.
    query =
      e.text !== ""
        ? query.where("label", props.searchMethod || "startswith", e.text, true)
        : query;
    // Pass the filter data source, filter query to updateData method.
    const searchData: OptionValues = getOptionValues(props.optionValues);
    e.updateData(searchData, query);
  };

  const optionsPredicate = useCallback(
    (option: Option<EnumSingleSelectFieldValue>) => {
      // Always include the selected value.
      if (option.value === localValue) {
        return true;
      }

      // If a filter function is provided, use it to filter the options.
      if (props.optionsFilter) {
        return props.optionsFilter(option);
      }

      // If no filter function is provided, include all options.
      return true;
    },
    [localValue, props.optionsFilter]
  );

  useEffect(() => {
    if (
      props.optionsFilter &&
      singleSelectRef.current &&
      dataSourceRef.current
    ) {
      const dataManager = dataSourceRef.current as DataManager;
      const oldJson = dataManager.dataSource.json;
      const newJson = getOptionValues(orderedOptionValues());
      const areEqual = isEqual(oldJson, newJson);
      if (!areEqual) {
        dataManager.dataSource.json = newJson;
        singleSelectRef.current.refresh();
      }
    }
  }, [optionsPredicate, props.optionsFilter, dataSourceRef.current]);

  const getOptionValues = (
    options: Option<EnumSingleSelectFieldValue>[]
  ): OptionValues => {
    const filteredOptions = options.filter(optionsPredicate);

    const searchData: OptionValues = filteredOptions.map((option) => ({
      label: option.label,
      value: option.value || {},
    }));

    return searchData;
  };

  const orderedOptionValues = () => {
    if (!!props.sortFunction) {
      props.optionValues.sort(props.sortFunction);
    }

    if (!!props.reorderOptions) {
      props.reorderOptions.forEach((element) => {
        const foundOptionIndex = (props.optionValues || []).findIndex(
          (x) => x.value === element.value
        );

        props.optionValues.splice(
          element.newIndex,
          0,
          props.optionValues.splice(foundOptionIndex, 1)[0]
        );
      });
    }
    return props.optionValues;
  };

  const defaultSettings: EnumSingleSelectPropType = {
    filterBarPlaceholder: "Search...",
    fields: { text: "label", value: "value" },
    filtering: filteringHandler,
    select: (args: SelectEventArgs) => {
      setLocalValue(args.itemData.value);
      props?.select?.(args);
    },
  };

  const setup = () => {
    // Merge settings
    if (props.optionsFilter) {
      dataSourceRef.current = new DataManager({
        json: getOptionValues(orderedOptionValues()),
        adaptor: new JsonAdaptor(),
      });
    } else {
      dataSourceRef.current = getOptionValues(orderedOptionValues());
    }

    const settings = {
      ...defaultSettings,
      // Do not pass layout props like width from field to the inner dropdown
      // Width is controlled by the container; dropdown should be 100% of field width
      ...omit(
        props,
        "showClearButton",
        "optionValues",
        "width",
        "expressionData",
        "fieldType",
        "type_AsString",
        "modelName",
        "fieldName",
        "fieldKey",
        "fieldPath",
        "fieldTypeName",
        "oDataKey",
        "isRequired",
        "typeId",
        "errorMessages"
      ),
      ...({
        dataSource: dataSourceRef.current,
        allowFiltering: !props.hideSearchBox,
        readonly: props.disabled,
      } as EnumSingleSelectPropType),
    } as EnumSingleSelectPropType;

    // set
    setSettings(settings);
    setLocalValue(props.value);
    setReady(true);
  };

  return {
    localValue,
    setLocalValue,
    settings,
    singleSelectRef,
    ready,
  };
};
